<template>
  <q-editor
    ref="editorRef"
    v-bind="$attrs"
    v-model="model"
    min-height="5rem"
    paragraph-tag="p"
    content-class="tw-prose"
    :toolbar="defaultToolbar"
    :definitions="definitions"
    @paste="onPaste"
    @keydown="onKeydown"
    @focus="focusInput"
  />
</template>

<script setup>
import {
  editorBold,
  editorCode,
  editorImage,
  editorItalic,
  editorLink,
  editorOrdered,
  editorUnderline,
  editorUnlink,
  editorUnordered,
  toDuotoneIcon,
  toClassicIcon,
} from "common/defaultIcons";
import { UPLOAD_LIMITS_MB } from "shared/constants";
import { useQuasar } from "quasar";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
  nextTick,
  watch,
} from "vue";

// Quasar
const $q = useQuasar();

const model = defineModel({
  type: String,
  default: "",
  required: true,
});

// Refs
const editorRef = useTemplateRef("editorRef");

const props = defineProps({
  headings: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Boolean,
    default: false,
  },
  link: {
    type: Boolean,
    default: false,
  },
  code: {
    type: Boolean,
    default: false,
  },
  minimal: {
    type: Boolean,
    default: false,
  },
  singleLine: {
    type: Boolean,
    default: false,
  },
});

const defaultToolbar = computed(() => {
  if (props.minimal) {
    return [["bold", "italic", "underline"]];
  }

  return [
    [
      {
        label: $q.lang.editor.align,
        icon: "fa-duotone fa-align-left",
        fixedLabel: true,
        list: "only-icons",
        options: ["left", "center", "right", "justify"],
      },
      ...(props.headings
        ? [
            {
              label: "Titel",
              options: ["p", "h1", "h2", "h3"],
            },
          ]
        : []),
    ],
    ["bold", "italic", "underline"],
    ["unordered", "ordered"],
    ...(props.code ? [["code"]] : []),
    ...(props.link ? [["link", "unlink"]] : []),
    ...(props.images ? [["images"]] : []),
  ];
});

const uploadImage = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";

  input.onchange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const maxSize = UPLOAD_LIMITS_MB.EDITOR_INLINE_IMAGE * 1024 * 1024;
    if (file.size > maxSize) {
      $q.notify({
        type: "negative",
        message: `Afbeelding is te groot. Maximaal toegestane grootte is ${UPLOAD_LIMITS_MB.EDITOR_INLINE_IMAGE}MB.`,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = function (e) {
      const imageUrl = e.target.result;
      insertImageToEditor(imageUrl);
    };
    reader.readAsDataURL(file);
  };

  input.click();
};

const insertImageToEditor = (src) => {
  const edit = editorRef.value;
  edit.caret.restore();
  edit.runCmd("insertHTML", `<img src="${src}" style="max-width: 100%;" />`);
  edit.focus();
};

const definitions = computed(() => {
  return {
    images: {
      icon: toDuotoneIcon(editorImage),
      tip: "Afbeelding invoegen",
      handler: uploadImage,
    },
    bold: {
      icon: toClassicIcon(editorBold, "solid"),
    },
    italic: {
      icon: toClassicIcon(editorItalic, "solid"),
    },
    underline: {
      icon: toClassicIcon(editorUnderline, "solid"),
    },
    unordered: {
      icon: toDuotoneIcon(editorUnordered),
    },
    ordered: {
      icon: toDuotoneIcon(editorOrdered),
    },
    link: {
      icon: toDuotoneIcon(editorLink),
      tip: "Link toevoegen",
      handler: () => {
        addLinkHandler();
      },
    },
    unlink: {
      icon: toDuotoneIcon(editorUnlink),
      tip: "Link verwijderen",
      handler: () => {
        removeLink();
      },
    },
    code: {
      icon: toClassicIcon(editorCode, "solid"),
    },
  };
});

const removeLink = () => {
  const edit = editorRef.value;
  if (!edit) {
    return;
  }

  edit.focus();
  edit.runCmd("unlink");
};

const addLinkHandler = () => {
  const edit = editorRef.value;

  const selection = window.getSelection();
  let savedRange = null;
  let selectedText = "";
  let selectedHref = "";

  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    savedRange = range.cloneRange();
    selectedText = selection
      .toString()
      .replace(/\u200B/g, "")
      .trim();

    let node = selection.anchorNode;
    while (node && node.nodeType === 3) {
      node = node.parentNode;
    }
    const anchor = node?.closest?.("a");
    if (anchor && anchor.href) {
      selectedHref = anchor.href;
    }
  }

  const isValidUrl = (text) => {
    try {
      const url = new URL(text);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const initialUrl = isValidUrl(selectedHref)
    ? selectedHref
    : isValidUrl(selectedText)
      ? selectedText
      : "https://";

  const initialTitle = selectedText || "";

  $q.dialog({
    title: "Voer de URL in",
    message: "Plak of typ de URL voor de link:",
    prompt: {
      model: initialUrl,
      type: "text",
      label: "URL",
      isValid: (val) => isValidUrl(val),
      rules: [
        (val) => !!val || "URL is verplicht",
        (val) => isValidUrl(val) || "URL moet geldig zijn (http/https)",
      ],
    },
    cancel: true,
    persistent: true,
  }).onOk((url) => {
    $q.dialog({
      title: "Linktitel",
      message: "Tekst die zichtbaar moet zijn als link:",
      prompt: {
        model: initialTitle,
        type: "text",
        label: "Titel",
      },
      cancel: true,
      persistent: true,
    }).onOk((title) => {
      setTimeout(() => {
        edit.focus();

        if (savedRange) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(savedRange);

          savedRange.deleteContents();
        }

        const safeTitle = title.replace(/"/g, "&quot;");
        edit.runCmd(
          "insertHTML",
          `<a href="${url}" target="_blank" rel="noopener noreferrer">${safeTitle}</a>`,
        );

        edit.focus();
      }, 0);
    });
  });
};

// A single-line editor may never grow a second line. Enter is swallowed on the
// content element only, so the toolbar buttons stay operable by keyboard, and a
// pasted newline arrives as a space instead of a break.
const onKeydown = (evt) => {
  if (props.singleLine === false || evt.key !== "Enter") {
    return;
  }

  if (evt.target === editorRef.value?.getContentEl()) {
    evt.preventDefault();
  }
};

const flattenPaste = (text) =>
  props.singleLine === true ? text.replace(/\s*[\r\n]+\s*/g, " ") : text;

const onPaste = (evt) => {
  // Let inputs do their thing, so we don't break pasting of links.
  if (evt.target.nodeName === "INPUT") {
    return;
  }
  let text, onPasteStripFormattingIEPaste;
  evt.preventDefault();
  evt.stopPropagation();
  if (evt.originalEvent && evt.originalEvent.clipboardData?.getData) {
    text = evt.originalEvent.clipboardData.getData("text/plain");
    editorRef.value.runCmd("insertText", flattenPaste(text));
  } else if (evt.clipboardData?.getData) {
    text = evt.clipboardData.getData("text/plain");
    editorRef.value.runCmd("insertText", flattenPaste(text));
  } else if (window.clipboardData?.getData) {
    if (!onPasteStripFormattingIEPaste) {
      onPasteStripFormattingIEPaste = true;
      editorRef.value.runCmd("ms-pasteTextOnly", text);
    }
    onPasteStripFormattingIEPaste = false;
  }
};

/* -------------------------------------------
   Keep the content normalised: it always starts with <p>…</p>, links open
   in a new tab and no inline font-size survives. Runs once on mount, after
   every edit, and for a model value that did not come from the DOM.
-------------------------------------------- */

const INLINE_TAGS = new Set([
  "B",
  "STRONG",
  "I",
  "EM",
  "U",
  "SPAN",
  "A",
  "CODE",
  "S",
  "DEL",
  "SUB",
  "SUP",
  "SMALL",
  "MARK",
  "IMG",
]);

// Hands a DOM rewrite to QEditor the way a keystroke would. Writing the HTML
// to the model instead makes QEditor reassign innerHTML, and Firefox then
// drops the caret to the content root (#1728).
let syncing = false;
const syncModelFromDom = (editorElement) => {
  syncing = true;
  editorElement.dispatchEvent(new Event("input", { bubbles: true }));
  syncing = false;
};

const placeCaret = (node, offset) => {
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  const selection = document.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};

const placeCaretAtEnd = (block) => {
  const last = block.lastChild;
  if (last?.nodeType === Node.TEXT_NODE) {
    placeCaret(last, last.length);
    return;
  }
  if (last?.nodeName === "BR") {
    placeCaret(block, block.childNodes.length - 1);
    return;
  }
  placeCaret(block, block.childNodes.length);
};

const caretIsInside = (editorElement) => {
  const anchor = document.getSelection()?.anchorNode ?? null;
  return anchor !== null && editorElement.contains(anchor);
};

const isInlineNode = (node) =>
  node.nodeType === Node.TEXT_NODE ||
  (node.nodeType === Node.ELEMENT_NODE && INLINE_TAGS.has(node.tagName));

// Wraps a leading run of text/inline nodes into a <p>; a first node that is
// already a block (<p>, <h1>, ...) is left alone. Moving the caret's text
// node collapses the selection to the editor root, so the caret is put back
// at the end of the paragraph. Returns true when the DOM changed.
const normalizeFirstBlock = (editorElement) => {
  const firstNode = editorElement.firstChild;
  if (firstNode === null || isInlineNode(firstNode) === false) {
    return false;
  }

  const restoreCaret = caretIsInside(editorElement);
  const p = document.createElement("p");
  while (
    editorElement.firstChild !== null &&
    isInlineNode(editorElement.firstChild)
  ) {
    p.appendChild(editorElement.firstChild);
  }
  if (p.innerHTML === "") {
    p.innerHTML = "<br>";
  }
  editorElement.insertBefore(p, editorElement.firstChild);
  if (restoreCaret === true) {
    placeCaretAtEnd(p);
  }
  return true;
};

// Returns true when the DOM changed.
const normalizeAnchors = (editorElement) => {
  let changed = false;
  editorElement.querySelectorAll("a").forEach((anchor) => {
    if (anchor.getAttribute("target") !== "_blank") {
      anchor.setAttribute("target", "_blank");
      changed = true;
    }
    if (anchor.getAttribute("rel") !== "noopener noreferrer") {
      anchor.setAttribute("rel", "noopener noreferrer");
      changed = true;
    }
  });
  return changed;
};

// Remove inline font-size from any node inside root; return true if changes were made
const stripInlineFontSizes = (rootEl) => {
  let changed = false;
  const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode();
  while (node) {
    const el = node;
    if (el.style && el.style.fontSize) {
      el.style.removeProperty("font-size");
      // Clean empty style attributes
      if (el.style.cssText.trim() === "") {
        el.removeAttribute("style");
      }
      changed = true;
    } else if (el.hasAttribute && el.hasAttribute("style")) {
      // Fallback: if style attribute contains font-size in raw text (edge cases)
      const styleText = el.getAttribute("style") || "";
      if (/font-size\s*:/i.test(styleText)) {
        // Remove only the font-size declaration
        const cleaned = styleText
          .split(";")
          .map((s) => s.trim())
          .filter((s) => s.length > 0 && !/font-size\s*:/i.test(s))
          .join("; ");
        if (cleaned.length > 0) {
          el.setAttribute("style", cleaned);
        } else {
          el.removeAttribute("style");
        }
        changed = true;
      }
    }
    node = walker.nextNode();
  }
  return changed;
};

const normalizeContent = (editorElement) => {
  const firstBlock = normalizeFirstBlock(editorElement);
  const anchors = normalizeAnchors(editorElement);
  const fontSizes = stripInlineFontSizes(editorElement);
  return firstBlock || anchors || fontSizes;
};

const normalizeAndSync = (editorElement) => {
  if (normalizeContent(editorElement) === true) {
    syncModelFromDom(editorElement);
  }
};

// The HTML the DOM last reported; a model value equal to it needs no pass.
let lastDomValue = null;

// Runs after QEditor's own input handler emitted the raw HTML. Registered on
// the content element itself: QEditor binds its handler after the listeners
// it receives through attrs, so an @input on <q-editor> never fires.
const onContentInput = (evt) => {
  lastDomValue = evt.currentTarget.innerHTML;
  if (syncing === true) {
    return;
  }
  normalizeAndSync(evt.currentTarget);
};

const focusInput = () => {
  const editorEl = editorRef.value.getContentEl();

  // Seed an empty paragraph if needed. Firefox leaves the caret at the
  // content root after the rewrite, so it is moved into the paragraph.
  if (!model.value || !model.value.trim()) {
    editorEl.innerHTML = "<p><br></p>";
    placeCaret(editorEl.firstChild, 0);
    syncModelFromDom(editorEl);
  }
};

onMounted(async () => {
  await nextTick();

  if (!editorRef.value) {
    return;
  }

  const editorEl = editorRef.value.getContentEl();

  normalizeAndSync(editorEl);

  editorEl.addEventListener("input", onContentInput);

  // A programmatic value (loading a task) is written into the DOM by
  // QEditor's own pre-flush watcher; post-flush runs after it.
  watch(
    () => model.value,
    () => {
      if (model.value === lastDomValue) {
        return;
      }
      normalizeAndSync(editorEl);
    },
    { flush: "post" },
  );
});

onBeforeUnmount(() => {
  editorRef.value?.getContentEl()?.removeEventListener("input", onContentInput);
});
</script>

<style lang="scss">
.q-editor {
  @apply tw-rounded-lg;

  &__content {
    @apply tw-prose;

    a {
      @apply tw-cursor-text;
    }
  }
}
</style>
